import { Observable, ReplaySubject } from "rxjs";
import { withRequestIdleCallbackAsync } from "../../annotations/request-idle-callback.annotation";
import { BooleanViewState, DropdownOptionViewState, DropdownViewState, GroupViewState, RangeViewState, RootOptionViewState, SelectableOptionViewState, SubgroupViewState, TextInputDropdownViewState, TextInputViewState, ViewState } from "./convert-options.view-states";

export interface ConvertOptionsSummary {
    [key: string]: any;
}

export interface ConvertOptionsContextObserver {
    readonly modelChangeObserver: Observable<ConvertOptionsSummary>;
}

export interface ConvertOptionsProvider {
    getModel(): RootOptionViewState;
    getSummary(): { [key: string]: any }
    getOptionValue(propertyName: string): any
}

export class ConvertOptionsContextV2 implements ConvertOptionsContextObserver, ConvertOptionsProvider {
    private modelChanged = new ReplaySubject<ConvertOptionsSummary>(1);

    readonly modelChangeObserver = this.modelChanged.asObservable();
    private rootState: RootOptionViewState;
    private viewStatesByPropertyName: { [key: string]: ViewState[] } = {};
    private initialSummary: ConvertOptionsSummary;
    /** Properties written ONLY by value::unit fields (TEXT_INPUT_DROPDOWN) -- see `getSummary`. */
    private valueUnitPropertyNames = new Set<string>();

    @withRequestIdleCallbackAsync
    async emitModelChanged() {
        this.modelChanged.next(this.getSummary());
    }

    private constructor(options) {
        this.viewStatesByPropertyName = {};
        this.rootState = this.init(options);
        this.markSharedPropertyGroups();
        this.valueUnitPropertyNames = this.collectValueUnitPropertyNames();
        // The TREE's summary, empty value::unit fields included as '': `resetToDefaults` restores
        // from it, and `restoreDefaultValues` skips an absent key -- so a start / end / duration
        // whose default is empty must be in here for a reset to clear what the user typed.
        this.initialSummary = this.summarizeTree();
    }

    /** Flag GROUPs whose property is also written by another GROUP (e.g. `compression`
     *  on IMAGE_COMPRESSION vs GIF_FILE_COMPRESSION) so a hidden one never wipes the
     *  visible owner's value during summarize. Same rule for SUBGROUP twins
     *  (per-context value variants with disjoint hidden clauses, e.g. the
     *  RANGE_100/RANGE_500 sizePercentage pair): a hidden sharer must neither
     *  seed the view results nor unset the visible owner's value. */
    private markSharedPropertyGroups() {
        Object.values(this.viewStatesByPropertyName).forEach(states => {
            const groups = states.filter(it => it instanceof GroupViewState) as GroupViewState[];
            if (groups.length > 1) {
                groups.forEach(g => g.propertyIsSharedWithSiblingGroup = true);
            }
            const subgroups = states.filter(it => it instanceof SubgroupViewState) as SubgroupViewState[];
            if (subgroups.length > 1) {
                subgroups.forEach(s => s.propertyIsSharedWithSiblingSubgroup = true);
            }
        });
    }

    /**
     * Every property whose only writers are value::unit fields (`TextInputDropdownViewState`, the
     * `"4::mbps"` / `"10::SECONDS"` / `"1280::PIXEL"` shells). A property that any GROUP or any other
     * value type also writes is left out, so '' from a plain TEXT_INPUT is never touched here.
     */
    private collectValueUnitPropertyNames(): Set<string> {
        const names = new Set<string>();
        Object.entries(this.viewStatesByPropertyName).forEach(([name, states]) => {
            const valueUnitOnly = states.length > 0 && states.every(state =>
                state instanceof SubgroupViewState
                && state.options?.[state.propertyNames.indexOf(name)] instanceof TextInputDropdownViewState);
            if (valueUnitOnly) names.add(name);
        });
        return names;
    }

    static newContext(options) {
        return new ConvertOptionsContextV2(options);
    }

    resetToDefaults() {
        this.applySummary(this.initialSummary);
    }

    /**
     * The summary as built, before any user interaction.
     *
     * `initialSummary` has existed since this class was written (`:36`) and `resetToDefaults`
     * already restores from it; the only thing missing was a way to READ it, which Quick convert's
     * single-photo hold rule needs so it can tell "unchanged" from "chosen"
     * (STUDIO_DESIGN.md §9 item 2, controller ruling 3).
     *
     * It is the SNAPSHOT, not a fresh `getSummary()`: the converter page mutates this context as
     * files arrive (`file-convert.component.tsx:292-303` writes `uploadedFileExtensions`), so a
     * re-derived "default" would move under the caller and the comparison would be meaningless.
     *
     * RETURNED BY IDENTITY, SO DO NOT MUTATE IT. This is the same object `resetToDefaults` applies
     * (`:63`), so a caller that writes into it silently redefines what "reset" means for the rest
     * of the page's life. Read it, or spread it.
     *
     * It is the TREE's summary, not a request body: an empty value::unit field is '' in it (so a
     * reset clears it), where `getSummary()` leaves the key out. Never post it.
     */
    getDefaultSummary(): ConvertOptionsSummary {
        return this.initialSummary;
    }

    getModel() {
        return this.rootState;
    }

    setRootProperty(propertyName: string, propertyValue: string | string[]) {
        this.rootState.setProperty(propertyName, propertyValue);
        this.emitModelChanged();
    }

    applySummary(summary: ConvertOptionsSummary) {
        this.rootState.restoreDefaultValues(summary);
        // Force update to trigger context switches (e.g. changing compression mode)
        // which might restore default values for that context
        this.summarizeTree();
        // Re-apply user summary to ensure their specific values persist over context defaults
        this.rootState.restoreDefaultValues(summary);
        this.emitModelChanged();
    }

    getRootProperty(propertyName: string) {
        return this.rootState.getPropertyValue(propertyName);
    }

    unsetRootProperty(propertyName: string) {
        this.rootState.unsetProperty(propertyName);
        this.emitModelChanged();
    }

    findViewStateByPropertyName(propertyName: string) {
        return this.getModel().findByPropertyName(propertyName);
    }

    findNode(startNode: ViewState, predicate: (id: string) => boolean): ViewState | undefined {
        return this.getModel().findNode(startNode, predicate);
    }


    /**
     * The summary every caller reads and every request is built from -- Quick convert
     * (`prepareMergeOptionsAndSnapshot`), the Frame picker (through `host.convertFiles`), Studio's
     * `buildExportRequest`, the estimate probe, the model-change emission -- with every EMPTY
     * value::unit field left OUT (key absent), never `''` and never `"::UNIT"`.
     *
     * Why absent: since R14Q-13 (3663714fc) an empty field reads `''`, and ironman cannot parse it --
     * `getBitrateV2` splits `videoBitrate` on "::" and reads the unit, `decodeTrimValue` throws
     * "Unit  is not supported" (the 26 failed prod jobs after the 2026-09-27 release: 11
     * `videoBitrate: ""`, 15 `videoTrimDurationTime: ""`). `"::UNIT"` (the pre-R14Q-13 shape) is no
     * safer (`"".toFloat()` throws). Every reader is `getStringOrDefault(key)?.let { ... }`, so an
     * absent key is the one shape it reads as "not set" -- and the one pre-release traffic carried.
     * A summary chip reads absent and '' alike as unset (`utils/option-echo.ts` `has`), so the
     * display keeps R14Q-13's "an empty field is unset".
     */
    getSummary(): ConvertOptionsSummary {
        return this.withoutEmptyValueUnits(this.summarizeTree());
    }

    /** The tree's own summary: empty value::unit fields are '' here (restore snapshots only). */
    private summarizeTree(): ConvertOptionsSummary {
        const model = this.getModel();
        const result = model.getResultForView();
        return model.summarize(result);
    }

    /** Drops the empty value::unit keys from a FRESH summary (`summarize` builds a new object per call). */
    private withoutEmptyValueUnits(summary: ConvertOptionsSummary): ConvertOptionsSummary {
        if (!summary || typeof summary !== 'object') return summary;
        this.valueUnitPropertyNames.forEach(name => {
            if (name in summary && TextInputDropdownViewState.isEmptyValue(summary[name])) delete summary[name];
        });
        return summary;
    }

    getOptionValue(propertyName: string) {
        const viewStates = this.getViewStates(propertyName);
        return {}
    }

    getViewStates(propertyName: string) {
        return this.viewStatesByPropertyName[propertyName];
    }

    addViewStateToPropertyName(propertyName: string, viewState: ViewState) {
        if (!this.viewStatesByPropertyName[propertyName]) {
            this.viewStatesByPropertyName[propertyName] = [];
        }
        this.viewStatesByPropertyName[propertyName].push(viewState);
    }

    private init(option): RootOptionViewState {
        const selectableOption = new RootOptionViewState();
        if (this.isRootOption(option)) {
            selectableOption.name = option.name;
            selectableOption.id = option.id;
            selectableOption.properties = option.properties;
            selectableOption.options = option.options.map(it => this.initInternal(it, selectableOption));
            return selectableOption;
        }
        throw Error("Cannot init options with an null object");
    }

    private initInternal(option, parent: ViewState) {
        if (option) {
            if (option && this.isSelectableOption(option)) {
                // console.log(`selectableOption ${option.id}`)
                const selectableOption = new SelectableOptionViewState();
                selectableOption.id = option.id;
                selectableOption.name = option.name;
                selectableOption.propertyValue = option.propertyValue;
                selectableOption.iconPath = option.iconPath;
                selectableOption.options = option.options.map(it => this.initInternal(it, selectableOption));
                selectableOption.parent = parent;
                selectableOption.selected = option.selected;
                selectableOption.hidden = option.hidden;
                selectableOption.notes = option.notes;
                selectableOption.optionLevel = option.optionLevel;
                selectableOption.whenFeatureFlag = option.whenFeatureFlag;
                return selectableOption;
            }


            // Group create a section
            // With in it, we display the selectable items, seek bar or anything else.
            else if (option && this.isGroupOption(option)) {
                const group: GroupViewState = new GroupViewState();
                group.id = option.id;
                group.name = option.name;
                group.propertyNames = option.propertyNames;
                group.hidden = option.hidden;
                group.notes = option.notes;
                group.options = option.options.map(it => this.initInternal(it, group));
                group.selected.selected = true;
                group.defaultOptionId = group.selected.id;
                group.maxButtonOptions = option.maxButtonOptions;
                group.parent = parent;
                group.restoreStateContexts = option.restoreStateContexts;
                group.initialState = group.getSnapshot();
                group.optionLevel = option.optionLevel;

                option.propertyNames.forEach(propertyName => {
                    this.addViewStateToPropertyName(propertyName, group);
                });
                return group;
            }
            else if (option && this.isSubgroupOption(option)) {
                const group = new SubgroupViewState();
                group.id = option.id;
                group.name = option.name;
                group.propertyNames = option.propertyNames;
                group.type = option.type;
                group.hidden = option.hidden;
                group.options = option.options.map(it => this.initInternal(it, group));
                group.notes = option.notes;
                group.parent = parent;

                option.propertyNames.forEach(propertyName => {
                    this.addViewStateToPropertyName(propertyName, group);
                });
                return group;
            } else {
                const vs = this.createViewStateForOption(option);
                vs.parent = parent;
                return vs
            }
        }
        throw Error("Cannot call internal init options with an null object");
    }


    valueTypes = new Set([
        'RANGE',
        'TEXT_INPUT',
        'TEXT_INPUT_DROPDOWN',
        'STRING',
        'BIT_RATE_TEXT_INPUT_DROPDOWN',
        'BOOLEAN',
        'DROPDOWN'
    ]);
    // private createViewStateForOptions(option) {
    //     return option.options.map(this.createViewStateForOption);
    // }


    private createViewStateForOption(option,) {
        if (!this.valueTypes.has(option.uiType)) {
            throw Error(`Value type not supported ${option.uiType} from ${option.id}`);
        }
        if (option.uiType == 'RANGE') {
            const range = new RangeViewState();
            range.id = option.id;
            range.type = option.type;
            range.max = option.max;
            range.min = option.min;
            range.step = option.step;
            range.value = option.value;
            // );
            return range;
        } else if (option.uiType == "BOOLEAN") {
            const obj = new BooleanViewState();
            obj.id = option.id;
            obj.type = option.type;
            obj.name = undefined;
            obj.value = option.value;
            return obj;
        } else if (option.uiType == "TEXT_INPUT") {
            const obj = new TextInputViewState();
            obj.id = option.id;
            obj.type = option.type;
            obj.name = undefined;
            obj.value = option.value;
            return obj;
        } else if (option.uiType == "TEXT_INPUT_DROPDOWN" || option.uiType == "BIT_RATE_TEXT_INPUT_DROPDOWN") {
            const dropdown = new DropdownViewState();
            dropdown.options = option.options.map(op => {
                const option = new DropdownOptionViewState();
                option.id = op.id;
                option.type = option.type;
                option.name = op.name;
                option.value = op.value;
                option.selected = op.selected;
                option.hidden = op.hidden;
                option.parent = dropdown;
                return option;
            });
            dropdown.selected.selected = true;

            const obj = new TextInputDropdownViewState();
            obj.id = option.id;
            obj.type = option.type;
            obj.name = option.name;
            obj.value = option.value;
            obj.dropdown = dropdown;
            return obj;
        } else if (option.uiType == 'DROPDOWN') {
            const dropdown = new DropdownViewState();
            dropdown.options = option.options.map(op => {
                const option = new DropdownOptionViewState();
                option.id = op.id;
                option.type = op.type;
                option.name = op.name;
                option.value = op.value;
                option.selected = op.selected;
                option.hidden = op.hidden;
                option.data = op.data;
                option.parent = dropdown;
                // GROUP_SELECTION entries can carry nested subgroups that reveal
                // while the entry is selected (same reveal-on-selection pattern as
                // groups). Hydrate them through the normal pipeline so their
                // propertyNames register for shared-property marking.
                if (op.options && op.options.length > 0) {
                    option.options = op.options.map(child => this.initInternal(child, option)) as any;
                }
                return option;
            });
            return dropdown;
        }
    }

    groupTypes = new Set(["GROUP"]);
    private isGroup(options: { uiType: string; }[]) {
        return options.every(this.isGroupOption);
    }

    private isGroupOption(options: { uiType: string; }) {
        return this.groupTypes.has(options.uiType);
    }


    subgroupTypes = new Set(["SUBGROUP"]);
    private isSubgroup(options: { uiType: string; }[]) {
        return options.every(this.isSubgroupOption);
    }

    private isSubgroupOption(options: { uiType: string; }) {
        return this.subgroupTypes.has(options.uiType);
    }

    selectTypes = new Set([
        'GROUP_SELECTION'
    ]);
    private isSelectableOptions(options: { uiType: string; }[]) {
        return options.every(this.isSelectableOption);
    }

    private isSelectableOption(option: { uiType: string; }) {
        return this.selectTypes.has(option.uiType);
    }

    rootTypes = new Set([
        'ROOT'
    ]);
    private isRootOption(option: { uiType: string; }) {
        return this.rootTypes.has(option.uiType);
    }

    private getSelectedOrFirst(options: { uiType: string; selected: boolean; value: string; }[]) {
        return options.find(it => it.selected) ?? options[0];
    }

}
